import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Spinner,
  Table,
  Button,
  Progress,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import PeriodFilter, { lastMonthToToday, periodToParams } from "../../components/Common/PeriodFilter";
import { getRoomAnalytics, exportRoomAnalytics } from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class RoomAnalytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      period: lastMonthToToday(),
      analyticsData: null,
      breadcrumbItems: [
        { title: "Analytics", link: "/analytics" },
        { title: "Room Analytics", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadAnalytics();
  }

  loadAnalytics = async () => {
    this.setState({ loading: true });
    const { period } = this.state;

    const response = await getRoomAnalytics(periodToParams(period));
    this.setState({
      analyticsData: response?.success ? response.data : null,
      loading: false,
    });
  };

  handlePeriodChange = (period) => {
    this.setState({ period }, this.loadAnalytics);
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const { period } = this.state;
      const res = await exportRoomAnalytics(periodToParams(period));
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  render() {
    const { loading, period, analyticsData } = this.state;

    const summary = analyticsData?.summary || {};
    const popularRooms = analyticsData?.popularRooms || [];
    const bookingTrend = analyticsData?.bookingTrend || [];
    const avgStayDuration = analyticsData?.avgStayDuration || 0;
    const cancellationRate = analyticsData?.cancellationRate || 0;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Room Analytics" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={8} className="mb-3 mb-md-0">
                    <Label>Period</Label>
                    <PeriodFilter value={period} onChange={this.handlePeriodChange} />
                  </Col>
                  <Col md={4} className="text-md-right">
                    <Button color="success" disabled={this.state.exporting} onClick={this.handleExport}>
                      {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                    </Button>
                    <Link to="/analytics" className="btn btn-outline-secondary ml-2">
                      Back to Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading room analytics...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <Row>
                  <Col md={3}>
                    <Card className="bg-warning text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Bookings</h6>
                        <h3>{this.formatNumber(summary.totalBookings)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-primary text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Revenue</h6>
                        <h3>{this.formatCurrency(summary.totalRevenue)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-info text-white">
                      <CardBody>
                        <h6 className="text-white-50">Avg Stay Duration</h6>
                        <h3>{avgStayDuration.toFixed(1)} days</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-danger text-white">
                      <CardBody>
                        <h6 className="text-white-50">Cancellation Rate</h6>
                        <h3>{cancellationRate.toFixed(1)}%</h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  {/* Popular Rooms */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Popular Rooms</h4>
                        {popularRooms.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Room</th>
                                <th className="text-right">Bookings</th>
                                <th className="text-right">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {popularRooms.slice(0, 10).map((room, index) => (
                                <tr key={room._id}>
                                  <td>{index + 1}</td>
                                  <td>{room.name || `Room #${room._id?.toString().slice(-4)}`}</td>
                                  <td className="text-right">{this.formatNumber(room.bookings)}</td>
                                  <td className="text-right">{this.formatCurrency(room.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Booking Trend */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Recent Booking Trend</h4>
                        {bookingTrend.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th className="text-right">Bookings</th>
                                <th className="text-right">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bookingTrend.slice(-10).map((item, index) => (
                                <tr key={index}>
                                  <td>{item._id}</td>
                                  <td className="text-right">{this.formatNumber(item.bookings)}</td>
                                  <td className="text-right">{this.formatCurrency(item.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Additional Stats */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Additional Metrics</h4>
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <p className="mb-1">Average Revenue per Booking</p>
                          <h5>{this.formatCurrency(summary.avgRevenue)}</h5>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <p className="mb-1">Total Guests</p>
                          <h5>{this.formatNumber(summary.totalGuests)}</h5>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <p className="mb-1">Cancellation Rate</p>
                          <Progress
                            value={cancellationRate}
                            color={cancellationRate > 20 ? "danger" : cancellationRate > 10 ? "warning" : "success"}
                            style={{ height: "20px" }}
                          >
                            {cancellationRate.toFixed(1)}%
                          </Progress>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(RoomAnalytics));
